// ============================================
// APPLICATION - Sucursales Hooks
// ============================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sucursalesRepository } from '../../infrastructure/http/sucursales.repository';
import { queryKeys } from '../query-keys';
import type { CrearSucursalDto, ActualizarSucursalDto, Sucursal } from '../../domain/dtos';

/**
 * Hook para obtener todas las sucursales
 */
export const useSucursales = () => {
  return useQuery({
    queryKey: queryKeys.sucursales.all,
    queryFn: () => sucursalesRepository.obtenerSucursales(),
    gcTime: 5 * 60 * 1000, // 5 minutos
    staleTime: 2 * 60 * 1000, // 2 minutos
    retry: 1,
    throwOnError: false,
  });
};

/**
 * Hook para obtener la sucursal actual del usuario autenticado
 */
export const useSucursalActual = () => {
  return useQuery({
    queryKey: ['sucursal-actual'],
    queryFn: () => sucursalesRepository.obtenerSucursalActual(),
    gcTime: 10 * 60 * 1000, // 10 minutos
    staleTime: 5 * 60 * 1000, // 5 minutos
    retry: 1,
    throwOnError: false,
  });
};

/**
 * Hook para crear una nueva sucursal
 */
export const useCrearSucursal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (datos: CrearSucursalDto) => sucursalesRepository.crearSucursal(datos),
    onSuccess: (nuevaSucursal) => {
      // Actualizar cache directamente agregando la nueva sucursal
      queryClient.setQueryData<Sucursal[]>(queryKeys.sucursales.all, (old) => {
        if (!old) return [nuevaSucursal];
        return [...old, nuevaSucursal];
      });
    },
  });
};

/**
 * Hook para eliminar una sucursal
 */
export const useEliminarSucursal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sucursalId: number) => sucursalesRepository.eliminarSucursal(sucursalId),
    onSuccess: (_, sucursalId) => {
      // Actualizar cache directamente eliminando la sucursal
      queryClient.setQueryData<Sucursal[]>(queryKeys.sucursales.all, (old) => {
        if (!old) return [];
        return old.filter((sucursal) => sucursal.id !== sucursalId);
      });
    },
  });
};

/**
 * Hook para actualizar una sucursal
 */
export const useActualizarSucursal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sucursalId, datos }: { sucursalId: number; datos: ActualizarSucursalDto }) =>
      sucursalesRepository.actualizarSucursal(sucursalId, datos),
    onSuccess: (sucursalActualizada) => {
      // Actualizar cache directamente con los datos actualizados
      queryClient.setQueryData<Sucursal[]>(queryKeys.sucursales.all, (old) => {
        if (!old) return [sucursalActualizada];
        return old.map((sucursal) =>
          sucursal.id === sucursalActualizada.id ? sucursalActualizada : sucursal
        );
      });
    },
  });
};
